import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { GenericMinimumWageExpandableItemSection } from "../containers/GenericMinimumWageExpandableItemSection";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { LuxembourgMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/luxembourgMinimumWageConfigurationModel";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const qualificationTypes = [
  DriverQualificationsType.UnqualifiedDriver,
  DriverQualificationsType.QualifiedDriver,
]

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInner = [
    'EUR',
]

const allowedCurrencies = [
    'EUR',
]

const translateKey = 'luxemburgConfiguration'

const country = 'luxemburg'

export const LuxemburgMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<LuxembourgMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.luxembourgConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('luxemburgConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.luxembourgConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, luxembourgConfiguration: state })}
            handleCancel={() => setState(q.data.luxembourgConfiguration)}
            dataTestId="luxemburg-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.luxembourgConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <GenericMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        qualificationTypes={qualificationTypes} 
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey={translateKey}
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
